import _countBy from 'lodash/countBy';
import _mapKeys from 'lodash/mapKeys';

import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {getUserAccess} from 'common/report/util/saved-entities/utils';
import {Experiment} from 'apps/experiments/src/types';

export enum ListPageTrackingEvent {
  PageLoaded = `page loaded`,
  ListLoaded = `list loaded`,
  CreateExperiment = `create experiment`,
  CreateExperimentError = `create experiment error`,
  TalkToSales = `talk to sales`,
}

export enum PageTrackingEvent {
  PageLoaded = `page loaded`,
  ExposuresLoaded = `exposures loaded`,
  MetricLoaded = `statsig loaded`,
  MetricError = `statsig error`,
  ExploreMetric = `explore metric`,
  DuplicateExperiment = `duplicate experiment`,
  DuplicateExperimentError = `duplicate experiment error`,
  DeleteExperiment = `delete experiment`,
  DeleteExperimentError = `delete experiment error`,
  SelectMetric = `select metric`,
  ChangeGlobalFilter = `change global filter`,
  ChangeGlobalVariantFilter = `change global variant filter`,
  ChangeGlobalSearchFilter = `change global search filter`,
  CommitExperiment = `commit experiment`,
  RevertExperiment = `revert experiment`,
  CommitFeatureFlag = `commit feature flag`,
  RevertFeatureFlag = `revert feature flag`,
  UnselectMetric = `unselect metric`,
  CreateMetric = `create metric`,
  DeleteMetric = `delete metric`,
  ReorderMetric = `reorder metric`,
  StartEditSettings = `start edit settings`,
  CancelEditSettings = `cancel edit settings`,
  SaveEditSettings = `save edit settings`,
  EditName = `edit name`,
  EditDescription = `edit description`,
  EditHypothesis = `edit hypothesis`,
  Launch = `launch`,
  ForceComplete = `force complete`,
  Ship = `ship`,
  NotShip = `not ship`,
  Undo = `undo`,
  Redo = `redo`,
}

export enum TrackingEventSource {
  ListPage = `list page`,
  ExperimentPage = `page`,
  ExperimentPageBanner = `page banner`,
  ExperimentPageFilterBar = `page filter bar`,
  ExperimentPageMetrics = `page metrics`,
  ExperimentPageConfiguration = `page configuration`,
  ExperimentPageMoreActions = `page more actions`,
  ExperimentPageMetricsModal = `page metrics modal`,
  ExperimentPageSettingsModal = `page settings modal`,
  ExperimentPageSetupModal = `page setup modal`,
}

type TrackExperimentListPageActionParams = {
  event: ListPageTrackingEvent;
  props?: Obj;
  source?: TrackingEventSource;
};
export function trackExperimentListPageAction({event, props, source}: TrackExperimentListPageActionParams) {
  props = props || {};
  mixpanel.track(`[Experiment List Page] ${event}`, {
    ...getCommonProps({source}),
    ...props,
  });
}

type TrackExperimentPageActionParams = {
  event: PageTrackingEvent;
  experiment?: Experiment;
  props?: Obj;
  source?: TrackingEventSource;
};
export function trackExperimentPageAction({event, experiment, props, source}: TrackExperimentPageActionParams) {
  props = props || {};
  mixpanel.track(`[Experiment Page] ${event}`, {
    ...getCommonProps({source}),
    ...getCommonPageTrackingProps({experiment}),
    ...props,
  });
}

function getCommonProps({source}: {source?: TrackingEventSource}): Obj {
  return {
    'Request-URL': window.location.href,
    timestamp_ms: Date.now(),
    ...(source ? {source} : {}),
  };
}

function getCommonPageTrackingProps({experiment}: {experiment?: Partial<Experiment>}): Obj {
  return {
    'experiment id': experiment?.id,
    'experiment creator id': experiment?.creator_id,
    'experiment name': experiment?.name,
    'experiment model': experiment?.settings?.testingModel,
    'experiment confidence level': experiment?.settings?.confidenceLevel,
    'experiment permission level': experiment
      ? getUserAccess(experiment, {
          ownerId: experiment.creator_id ?? 0,
          userId: userCookieUtils.getUserIdentityFromCookie()?.id ?? 0,
        })
      : null,
    ...getCommonMetricTypeCounts({experiment}),
    ...getCommonDurationTrackingProps({experiment}),
  };
}

function getCommonMetricTypeCounts({experiment}: {experiment?: Partial<Experiment>}): Obj {
  const metricTypeCounts = _countBy(experiment?.metrics, (metric) => metric?.type);
  return _mapKeys(metricTypeCounts, (_: number, key: string) => `experiment ${key} metrics - length`);
}

function getCommonDurationTrackingProps({experiment}: {experiment?: Partial<Experiment>}): Obj {
  const startDate = experiment?.start_date;
  if (!startDate) {
    return {};
  }
  const endDate = experiment?.end_date;
  const today = new Date();
  const endDateObj = endDate ? new Date(endDate) : today;
  const startDateObj = new Date(startDate);
  const diffTime = Math.abs(endDateObj.getTime() - startDateObj.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return {'experiment duration - days': diffDays};
}
